/**
 * Subscription builder discount calculation.
 *
 * Toggle `USE_MAX_PLAN_UNIFORM_DISCOUNT` to switch modes.
 * Legacy implementation lives in `subscriptionDiscountCalc.legacy.js`.
 */

import {
  FULL_DAY_MEAL_LABEL,
  getFullDayMealDiscountPercent,
  getPlanMenuSlotIds,
  parseSpecialtySlotProductId,
  resolveAddedSlotsChargePerDay,
} from '../../data/specialtyMealPlans';
import { dayDiscountFactorFromPercent } from '../../api/mealPlansApi';
import { roundMoney } from '../../utils/moneyFormat';
import { dayBandCardDiscountPercent, parseIsoDateUtc } from './subscriptionDayBands';
import {
  computeBuilderDayChargeLegacy,
  resolveAppliedDayDiscountLegacy,
} from './subscriptionDiscountCalc.legacy';

/** Set to `false` to restore legacy per-day / per-delivery-day discount behaviour. */
export const USE_MAX_PLAN_UNIFORM_DISCOUNT = true;

const DAY_NAMES_BY_INDEX = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

function buildSpecialtyGroups(products = []) {
  /** @type {Map<string, { planId: string, organic: boolean, isFullDayPackage: boolean, lines: Array<{ slotId: string, price: number, quantity: number, line: number }> }>} */
  const groups = new Map();

  (products || []).forEach((product) => {
    const ref = parseSpecialtySlotProductId(product.id);
    if (!ref) return;

    const quantity = Number(product.quantity || 1);
    const price = Number(product.price || 0);
    const line = price * (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
    const organic = Boolean(product.isOrganic);
    const isFullDayPackage = Boolean(product.isFullDayPackage && product.fullDayGroupId);
    const key = isFullDayPackage
      ? `fd|${product.fullDayGroupId}`
      : `${ref.planId}|${organic ? 1 : 0}|solo`;

    if (!groups.has(key)) {
      groups.set(key, {
        planId: ref.planId,
        organic,
        isFullDayPackage,
        lines: [],
      });
    }
    groups.get(key).lines.push({
      slotId: product.slotId || ref.slotId,
      price,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
      line,
    });
  });

  return groups;
}

/**
 * Highest discount % available in the cart + selected plan band, regardless of
 * which days/slots are scheduled.
 */
export function resolveMaxPlanDiscountPercent({
  products = [],
  getPlanById,
  volumeDiscountPercent = 0,
}) {
  let maxPercent = Math.min(100, Math.max(0, Number(volumeDiscountPercent) || 0));

  buildSpecialtyGroups(products).forEach((group) => {
    const plan = typeof getPlanById === 'function' ? getPlanById(group.planId) : null;
    if (!plan) return;

    const slotIds = [...new Set(group.lines.map((row) => row.slotId).filter(Boolean))];
    const listSum = group.lines.reduce((sum, row) => sum + row.line, 0);
    if (listSum <= 0) return;

    if (group.isFullDayPackage) {
      maxPercent = Math.max(maxPercent, getFullDayMealDiscountPercent(plan));
      return;
    }

    const menuSlotIds = getPlanMenuSlotIds(plan);
    const resolved = resolveAddedSlotsChargePerDay(plan, slotIds, menuSlotIds, group.organic);
    if (resolved == null) return;

    let charge = resolved;
    group.lines.forEach((row) => {
      if (row.quantity > 1) charge += row.price * (row.quantity - 1);
    });
    if (charge < listSum) {
      const pct = Math.round((1 - charge / listSum) * 100);
      maxPercent = Math.max(maxPercent, pct);
    }
  });

  return maxPercent;
}

function resolveUniformDiscountLabel(maxPercent, volumeDiscountPercent, products, getPlanById) {
  if (maxPercent <= 0) return null;
  if (maxPercent === Number(volumeDiscountPercent)) return 'Day subscription';

  let matchesFullDay = false;
  let matchesPackage = false;

  buildSpecialtyGroups(products).forEach((group) => {
    const plan = typeof getPlanById === 'function' ? getPlanById(group.planId) : null;
    if (!plan) return;

    if (group.isFullDayPackage && getFullDayMealDiscountPercent(plan) === maxPercent) {
      matchesFullDay = true;
      return;
    }

    const slotIds = [...new Set(group.lines.map((row) => row.slotId).filter(Boolean))];
    const listSum = group.lines.reduce((sum, row) => sum + row.line, 0);
    if (listSum <= 0) return;

    const menuSlotIds = getPlanMenuSlotIds(plan);
    const resolved = resolveAddedSlotsChargePerDay(plan, slotIds, menuSlotIds, group.organic);
    if (resolved == null) return;

    let charge = resolved;
    group.lines.forEach((row) => {
      if (row.quantity > 1) charge += row.price * (row.quantity - 1);
    });
    if (charge < listSum) {
      const pct = Math.round((1 - charge / listSum) * 100);
      if (pct === maxPercent) matchesPackage = true;
    }
  });

  if (matchesFullDay) return FULL_DAY_MEAL_LABEL;
  if (matchesPackage) return 'Plan package';
  return 'Plan discount';
}

/**
 * Apply one max eligible plan discount % uniformly to all scheduled lines on a day.
 */
export function computeBuilderDayChargeMaxPlanUniform({
  dayName,
  products = [],
  getPlanById,
  volumeDiscountFactor = 1,
  volumeDiscountPercent,
} = {}) {
  const resolvedVolumePercent = volumeDiscountPercent != null
    ? Number(volumeDiscountPercent) || 0
    : Math.round((1 - (Number.isFinite(volumeDiscountFactor) ? volumeDiscountFactor : 1)) * 100);

  const maxPercent = resolveMaxPlanDiscountPercent({
    products,
    getPlanById,
    volumeDiscountPercent: resolvedVolumePercent,
  });
  const factor = dayDiscountFactorFromPercent(maxPercent);

  let listActual = 0;
  (products || []).forEach((product) => {
    const productDays = Array.isArray(product.days) ? product.days : null;
    if (dayName && productDays && productDays.length > 0 && !productDays.includes(dayName)) {
      return;
    }

    const quantity = Number(product.quantity || 1);
    const price = Number(product.price || 0);
    listActual += price * (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
  });

  const final = listActual * factor;
  const discountSaving = listActual - final;
  const discountLabel = resolveUniformDiscountLabel(
    maxPercent,
    resolvedVolumePercent,
    products,
    getPlanById,
  );

  return {
    actual: listActual,
    chargedBeforeVolume: listActual,
    final,
    packageSaving: 0,
    volumeSaving: discountSaving,
    discountSaving,
    discountLabel,
    discountPercent: maxPercent,
    saving: discountSaving,
  };
}

/**
 * Period totals for uniform max-plan discount — sum list prices across days,
 * then apply discount once (2-decimal round) so review totals match subtotal × %.
 */
export function computeBuilderPeriodDiscountTotals({
  startDate,
  endDate,
  products = [],
  getPlanById,
  volumeDiscountFactor = 1,
  volumeDiscountPercent,
}) {
  const start = parseIsoDateUtc(startDate);
  const end = parseIsoDateUtc(endDate);
  if (!start || !end || end < start) {
    return {
      actual: 0,
      saving: 0,
      final: 0,
      packageSaving: 0,
      volumeSaving: 0,
      discountSaving: 0,
      discountLabel: null,
      discountPercent: 0,
    };
  }

  const resolvedVolumePercent = volumeDiscountPercent != null
    ? Number(volumeDiscountPercent) || 0
    : Math.round((1 - (Number.isFinite(volumeDiscountFactor) ? volumeDiscountFactor : 1)) * 100);

  const maxPercent = resolveMaxPlanDiscountPercent({
    products,
    getPlanById,
    volumeDiscountPercent: resolvedVolumePercent,
  });
  const factor = dayDiscountFactorFromPercent(maxPercent);
  const discountLabel = resolveUniformDiscountLabel(
    maxPercent,
    resolvedVolumePercent,
    products,
    getPlanById,
  );

  let actual = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const dayName = DAY_NAMES_BY_INDEX[cursor.getUTCDay()];
    (products || []).forEach((product) => {
      const productDays = Array.isArray(product.days) ? product.days : null;
      if (productDays && productDays.length > 0 && !productDays.includes(dayName)) return;

      const quantity = Number(product.quantity || 1);
      const qty = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      const addOns = Array.isArray(product.addOns) ? product.addOns : [];
      if (addOns.length > 0) {
        const hostUnit = Number(
          product.hostUnitPrice != null ? product.hostUnitPrice : product.price || 0,
        ) || 0;
        const addOnTotal = addOns.reduce(
          (sum, a) => sum + (Number(a.price) || 0) * Math.max(1, Math.round(Number(a.quantity) || 1)),
          0,
        );
        actual += hostUnit * qty + addOnTotal;
        return;
      }
      const price = Number(product.price || 0);
      actual += price * qty;
    });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  const final = roundMoney(actual * factor);
  const discountSaving = roundMoney(actual - final);

  return {
    actual,
    saving: discountSaving,
    final,
    packageSaving: 0,
    volumeSaving: discountSaving,
    discountSaving,
    discountLabel: maxPercent > 0 ? discountLabel : null,
    discountPercent: maxPercent,
  };
}

/**
 * Day-band tier for charge calculation.
 * Legacy: based on actual delivery-day count.
 * Uniform max: based on the selected plan band (independent of delivery-day count).
 */
export function resolveBuilderAppliedDayDiscount({
  visibleDayDiscounts = [],
  deliveryDayCount = 0,
  selectedDayBand = null,
}) {
  if (!USE_MAX_PLAN_UNIFORM_DISCOUNT) {
    return resolveAppliedDayDiscountLegacy({ visibleDayDiscounts, deliveryDayCount });
  }

  const band = selectedDayBand || { minDays: deliveryDayCount };
  const discountPercent = dayBandCardDiscountPercent(band, visibleDayDiscounts);
  return {
    discountPercent,
    minDays: Number(band.minDays) || null,
    label: band.title || band.label || null,
  };
}

export function computeBuilderDayCharge(opts = {}) {
  if (!USE_MAX_PLAN_UNIFORM_DISCOUNT) {
    return computeBuilderDayChargeLegacy(opts);
  }
  return computeBuilderDayChargeMaxPlanUniform(opts);
}

export { computeBuilderDayChargeLegacy } from './subscriptionDiscountCalc.legacy';
